import React, { useEffect, useMemo, useRef, useState } from 'react';

// --- Type Definitions ---
type SliceStyle = {
  backgroundColor: string;
  textColor: string;
};

type Slice = {
  label: string;
  style: SliceStyle;
};

interface WheelProps {
  slices: Slice[];
  winningSliceIndex: number | null;
  isSpinning: boolean;
  onSpinEnd: () => void;
  markerColor?: string;
  outerDotColor?: string;
  centerImage?: string;
}

// Easing function for smooth animation
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const Wheel: React.FC<WheelProps> = ({
  slices,
  winningSliceIndex,
  isSpinning,
  onSpinEnd,
  markerColor = '#FF3B30',
  outerDotColor = '#FFCC00',
  centerImage,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [rotation, setRotation] = useState(0);
  const size = 384;
  const isBrowser = typeof window !== 'undefined';

  const dotPositions = useMemo(() => {
    const count = Math.max(slices.length, 6);
    return Array.from({ length: count }, (_, idx) => {
      const angle = (2 * Math.PI * idx) / count;
      return {
        left: 50 + Math.cos(angle) * 47,
        top: 50 + Math.sin(angle) * 47,
      };
    });
  }, [slices.length]);

  // --- Drawing Logic ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const numSlices = slices.length;
    const anglePerSlice = (2 * Math.PI) / numSlices;
    const radius = size / 2;
    const sliceRadius = radius - 18;

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.translate(radius, radius);
    ctx.rotate(rotation);

    slices.forEach((slice, i) => {
      const startAngle = i * anglePerSlice;
      const endAngle = startAngle + anglePerSlice;

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, sliceRadius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = slice.style.backgroundColor;
      ctx.fill();

      const textAngle = startAngle + anglePerSlice / 2;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = slice.style.textColor;
      const baseFont = Math.min(18, Math.max(12, sliceRadius / (slice.label.length + 6)));
      ctx.font = `600 ${baseFont}px "Inter", "Poppins", "Arial"`;
      ctx.rotate(textAngle);
      ctx.translate(sliceRadius * 0.68, 0);

      const isUpsideDown = textAngle > Math.PI / 2 && textAngle < (3 * Math.PI) / 2;
      if (isUpsideDown) {
        ctx.rotate(Math.PI);
      }

      const maxWidth = sliceRadius * 0.7;
      const words = slice.label.split(' ');
      const lines: string[] = [];
      let currentLine = '';
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(testLine).width > maxWidth) {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) lines.push(currentLine);

      const lineHeight = baseFont * 1.1;
      const offset = -((lines.length - 1) * lineHeight) / 2;
      lines.forEach((line, index) => {
        ctx.fillText(line, 0, offset + index * lineHeight);
      });

      ctx.restore();
    });

    // --- Outer Ring Highlight ---
    ctx.beginPath();
    ctx.arc(0, 0, radius - 6, 0, Math.PI * 2);
    ctx.lineWidth = 4;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.stroke();

    ctx.restore(); // Restore to pre-rotation state

  }, [slices, rotation]);

  // --- Animation Logic ---
  useEffect(() => {
    if (!isSpinning || winningSliceIndex === null) return;

    const numSlices = slices.length;
    const anglePerSlice = (2 * Math.PI) / numSlices;

    // Calculate target angle
    const sliceCenter = (winningSliceIndex * anglePerSlice) + (anglePerSlice / 2);
    // The pointer is at the top (pointing down), which is PI/2 or 90deg.
    // We want the center of the winning slice to align with the pointer.
    const targetRotation = (2 * Math.PI) - sliceCenter + (Math.PI / 2);

    const randomSpins = 5 + Math.random() * 5; // 5 to 10 full spins
    const finalRotation = targetRotation + (randomSpins * 2 * Math.PI);

    let startTime: number;
    const duration = 5000; // 5 seconds

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      setRotation(easedProgress * finalRotation);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation finished
        onSpinEnd();
        // To ensure it stops at the exact spot and can be re-spun
        setRotation(finalRotation % (2 * Math.PI)); 
      }
    };

    if(isBrowser) {
        requestAnimationFrame(animate);
    }

  }, [isSpinning, winningSliceIndex, slices, onSpinEnd, isBrowser]);

  return (
    <div
      className="wheel-shell relative w-full max-w-[440px] aspect-square mx-auto"
      style={
        {
          '--marker-color': markerColor,
          '--accent-color': outerDotColor,
        } as React.CSSProperties
      }
    >
      <div className="wheel-rim-glow" />
      <div className="wheel-dot-container pointer-events-none">
        {dotPositions.map((dot, idx) => (
          <span
            key={`dot-${idx}`}
            className="wheel-dot"
            style={{
              left: `${dot.left}%`,
              top: `${dot.top}%`,
              animationDelay: `${idx * 0.12}s`,
              '--dot-color': outerDotColor,
            } as React.CSSProperties}
          />
        ))}
      </div>
      <canvas ref={canvasRef} width={size} height={size} className="w-full h-full rounded-full" />
      <div className="center-stack">
        <div className="center-pointer" />
        <div className="center-emblem">
          {centerImage ? (
            <img src={centerImage} alt="Merkez görseli" />
          ) : (
            <span>LOGO</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Wheel;
